/**
 * Order Completion (2026-08-16) — the ALWAYS-SHOWN confirm dialog copy.
 * Design: docs/superpowers/specs/2026-08-16-order-completion.md.
 *
 * Owner's resolved open-question #2: *"The confirm dialog ALWAYS shows — its
 * body reflects the order's state (there is never a no-dialog one-click
 * path)"* — either the combinable soft warnings, or a POSITIVE summary when
 * all is well: *"Order ORD-xx: all items delivered, paperwork attached —
 * confirm?"* One `ConfirmDialog`, its message computed from `{status,
 * all-items-received?, has-delivery-paperwork?}`.
 *
 * 🔴 **NEITHER WARNING BLOCKS — they inform.** The server does not look at
 * paperwork or received-state at all (`PATCH /api/orders/:id/complete`
 * enforces only status + authority); this dialog is the entire UX for those
 * two signals. Continue proceeds whatever it says; Cancel writes nothing.
 *
 * Shared by `DepartmentOrders.js` and `MyOrders.js` — ONE definition, so the
 * two surfaces cannot drift on the wording.
 */
import { getOrderNumber } from '../utils/orderTitle';
import { deliveryCountableItems } from '../utils/deliveryCountableItems';

export const ORDER_COMPLETE_CONFIRM_TITLE = 'Mark order complete?';
export const ORDER_REOPEN_CONFIRM_TITLE = 'Re-open this order?';

/**
 * `allItemsReceived(items)` — true when every line item on the order is
 * received, or the order has no items to check (nothing outstanding to warn
 * about — an empty/undefined `items` array reads as "no items", not
 * "unknown"; in practice this control only renders once the row's detail has
 * loaded, so `items` is always the real array by the time it can fire).
 *
 * 🪤 **`Boolean(item.is_received)`, NEVER `=== true`.** `order_items
 * .is_received` is stored as SQLite's boolean-as-INTEGER (`1`/`0`), and
 * `GET /api/orders/:id` sends it through untouched — the same convention
 * `DepartmentOrders.js`/`MyOrders.js` already follow at every OTHER read of
 * this column (`!!item.is_received`, never a strict-equality check). A
 * `=== true` read is falsy for the real value `1` and marks every delivered
 * order as having outstanding items — caught live: the seeded fixture for
 * this file's own screenshot walkthrough showed the warning on an order
 * whose "Received (2/2)" checkboxes were both ticked.
 */
export function allItemsReceived(items) {
  if (!Array.isArray(items)) return true;
  // ⚖️ GOODS ONLY (owner ruling 2026-09-19). This decides whether the completion
  // dialog warns *"This order still has items not marked delivered."* — and an
  // unticked adjustment row would raise that warning on an order where every
  // real thing has arrived, which is the churn the ruling exists to stop,
  // surviving in a dialog after the status itself was fixed.
  const countable = deliveryCountableItems(items);
  // 🪤 THE EMPTY ANSWER STAYS `true`, AND IT IS NOT THE SAME QUESTION
  // `utils/deliveryCountableItems.js#allGoodsReceived` ANSWERS — that one
  // requires `total > 0`, because "everything arrived" must be false for an
  // order with nothing on it. Here the meaning is *"is there anything
  // outstanding to warn about"*, and for no goods rows the honest answer is no.
  // Two questions, two answers; do not unify them.
  if (countable.length === 0) return true;
  return countable.every((item) => item && Boolean(item.is_received));
}

/**
 * Build the confirm dialog's title + message for marking an order complete.
 * Combines the two soft warnings when they apply, or renders the owner's
 * positive-summary wording when neither does.
 *
 * @param {{ order: object, items?: Array, hasDeliveryPaperwork?: boolean }} args
 *   `order` — the list row (status, order_number, reference_number).
 *   `items` — the order's line items, if loaded (for the outstanding-items
 *   check); omit/undefined reads as unknown, see `allItemsReceived`.
 *   `hasDeliveryPaperwork` — defaults to `order.has_delivery_paperwork`.
 */
export function buildCompleteConfirmMessage({ order, items, hasDeliveryPaperwork } = {}) {
  const orderLabel = getOrderNumber(order) || (order && order.reference_number) || 'this order';
  const paperwork = hasDeliveryPaperwork !== undefined ? hasDeliveryPaperwork : Boolean(order && order.has_delivery_paperwork);
  const outstandingItems = (order && order.status === 'partially_delivered') || !allItemsReceived(items);
  const noPaperwork = !paperwork;

  const warnings = [];
  if (outstandingItems) {
    warnings.push('This order still has items not marked delivered.');
  }
  if (noPaperwork) {
    warnings.push('No delivery paperwork is attached.');
  }

  if (warnings.length === 0) {
    return {
      title: ORDER_COMPLETE_CONFIRM_TITLE,
      message: `Order ${orderLabel}: all items delivered, paperwork attached — confirm?`,
    };
  }

  return {
    title: ORDER_COMPLETE_CONFIRM_TITLE,
    message: `${warnings.join(' ')} Mark it complete anyway?`,
  };
}

/** Re-open carries no warning — a light confirm is enough (owner-ruled). */
export function buildReopenConfirmMessage({ order } = {}) {
  const orderLabel = getOrderNumber(order) || (order && order.reference_number) || 'this order';
  return {
    title: ORDER_REOPEN_CONFIRM_TITLE,
    message: `Order ${orderLabel} will no longer show as complete. Continue?`,
  };
}
